import { Fragment, useMemo, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project-imports
import NavGroup from './NavGroup';
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';
import { MenuOrientation, HORIZONTAL_MAX_ITEM } from 'config';
import useConfig from 'hooks/useConfig';
import menuItem from 'menu-items';
import useAuth from 'hooks/useAuth';
import { getModuleAccess, parsePermissionsFromUser } from 'utils/permissionsFromUser';

// types
import { NavItemType } from 'types/menu';

function filterNavByPermissions(source: NavItemType[], user: unknown): NavItemType[] {
  const idx = parsePermissionsFromUser(user);
  return source
    .map((item) => {
      if (item.type === 'group' && item.children?.length) {
        const children = item.children.filter((child) => {
          if (idx === null) {
            return true;
          }
          if (child.navAlwaysAccessible === true) {
            return true;
          }
          const mod = child.permissionModule;
          if (!mod) {
            return false;
          }
          return getModuleAccess(idx, mod).read;
        });
        if (children.length === 0) return null;
        return { ...item, children };
      }
      return item;
    })
    .filter((x): x is NavItemType => x !== null);
}

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { user } = useAuth();

  const [selectedID, setSelectedID] = useState<string | undefined>('');
  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const menuItems = useMemo(
    () => ({ items: filterNavByPermissions([...menuItem.items], user) }),
    [user],
  );

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : 0;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId = '';

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item) => {
    if (item.type === 'group') {
      if (item.url && item.id !== lastItemId) {
        return (
          <Fragment key={item.id}>
            {menuOrientation !== MenuOrientation.HORIZONTAL && <Divider sx={{ my: 0.5 }} />}
            <NavItem item={item} level={1} isParents setSelectedID={setSelectedID} />
          </Fragment>
        );
      }
      return (
        <NavGroup
          key={item.id}
          selectedID={selectedID}
          setSelectedID={setSelectedID}
          setSelectedItems={setSelectedItems}
          setSelectedLevel={setSelectedLevel}
          selectedLevel={selectedLevel}
          selectedItems={selectedItems}
          lastItem={lastItem}
          remItems={remItems}
          lastItemId={lastItemId}
          item={item}
        />
      );
    }
    return (
      <Typography key={item.id} variant="h6" color="error" align="center">
        Fix - Navigation Group
      </Typography>
    );
  });

  let navTopPadding = 0;
  if (drawerOpen && !isHorizontal) {
    navTopPadding = 2;
  }

  return (
    <Box
      sx={{
        pt: navTopPadding,
        '& > ul:first-of-type': { mt: 0 },
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block',
        alignItems: 'center'
      }}
    >
      {navGroups}
    </Box>
  );
}
